import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notesAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  Plus, 
  StickyNote,
  Trash2,
  Filter,
  Globe,
  Sparkles,
  Share2,
  MoreHorizontal,
  Lightbulb,
  Edit2,
  X,
  Check,
  Lock,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const CATEGORY_CONFIG = {
  "Website": { icon: Globe, color: "bg-blue-50 text-blue-700 border-blue-200" },
  "Whatnot": { icon: Sparkles, color: "bg-purple-50 text-purple-700 border-purple-200" },
  "Social Media": { icon: Share2, color: "bg-pink-50 text-pink-700 border-pink-200" },
  "Miscellaneous": { icon: MoreHorizontal, color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
  "New Idea": { icon: Lightbulb, color: "bg-amber-50 text-amber-700 border-amber-200" },
};

const ImportantNotes = () => {
  const { user, activeBrand } = useAuth();
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [editingNote, setEditingNote] = useState(null);
  const [editText, setEditText] = useState('');
  
  const [newNote, setNewNote] = useState({
    description: '',
    category: 'Miscellaneous',
    visibility: 'private',
  });

  useEffect(() => {
    fetchData();
  }, [filterCategory, activeBrand]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [notesRes, categoriesRes] = await Promise.all([
        notesAPI.getAll(filterCategory !== 'all' ? filterCategory : null, activeBrand),
        notesAPI.getCategories(),
      ]);
      setNotes(notesRes.data);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      toast.error('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.description.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    try {
      await notesAPI.create(newNote);
      toast.success('Note added!');
      setShowAddSheet(false);
      setNewNote({
        description: '',
        category: 'Miscellaneous',
        visibility: 'private',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create note');
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await notesAPI.delete(id);
      toast.success('Note deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleStartEdit = (note) => {
    setEditingNote(note.id);
    setEditText(note.description);
  };

  const handleSaveEdit = async (id) => {
    if (!editText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }
    
    try {
      await notesAPI.update(id, { description: editText.trim() });
      toast.success('Note updated');
      setEditingNote(null);
      setEditText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditText('');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter notes by visibility
  const filteredNotes = filterVisibility === 'all' 
    ? notes 
    : notes.filter(n => n.visibility === filterVisibility);

  // Group notes by category
  const groupedNotes = filteredNotes.reduce((acc, note) => {
    const cat = note.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(note);
    return acc;
  }, {});

  // Check if current user can edit/delete a note
  const canEditNote = (note) => {
    return note.owner_id === user?.id;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6" data-testid="notes-loading">
        <div className="h-8 w-48 bg-zinc-100 rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-zinc-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="important-notes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-medium text-zinc-900"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Team Notes
          </h1>
          <p className="text-zinc-500 mt-1">
            Quick notes and ideas shared with your team
          </p>
        </div>
        
        <Button
          onClick={() => setShowAddSheet(true)}
          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
          data-testid="add-note-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Note
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48 border-zinc-200" data-testid="note-category-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterVisibility} onValueChange={setFilterVisibility}>
          <SelectTrigger className="w-40 border-zinc-200" data-testid="note-visibility-filter">
            <SelectValue placeholder="All Notes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Notes</SelectItem>
            <SelectItem value="private">
              <div className="flex items-center gap-2">
                <Lock className="w-3 h-3" /> My Private
              </div>
            </SelectItem>
            <SelectItem value="shared">
              <div className="flex items-center gap-2">
                <Users className="w-3 h-3" /> Shared
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        
        <span className="text-sm text-zinc-500">
          {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Notes Grid */}
      {filteredNotes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredNotes.map((note) => {
            const config = CATEGORY_CONFIG[note.category] || CATEGORY_CONFIG["Miscellaneous"];
            const Icon = config.icon;
            const isEditing = editingNote === note.id;
            const isOwner = canEditNote(note);
            
            return (
              <div 
                key={note.id}
                className={`bg-white border rounded-lg p-4 hover:shadow-md transition-all ${config.color.split(' ')[2]}`}
                data-testid={`note-${note.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${config.color}`}>
                      <Icon className="w-3 h-3" />
                      {note.category}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                      note.visibility === 'shared' 
                        ? 'bg-emerald-50 text-emerald-700' 
                        : 'bg-zinc-100 text-zinc-600'
                    }`}>
                      {note.visibility === 'shared' ? <Users className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      {note.visibility === 'shared' ? 'Shared' : 'Private'}
                    </span>
                  </div>
                  
                  {isOwner && (
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleSaveEdit(note.id)}
                            className="p-1 text-emerald-500 hover:text-emerald-600 transition-colors"
                            data-testid={`save-edit-${note.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-zinc-400 hover:text-zinc-600 transition-colors"
                            data-testid={`cancel-edit-${note.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStartEdit(note)}
                            className="p-1 text-zinc-300 hover:text-zinc-600 transition-colors"
                            data-testid={`edit-note-${note.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="p-1 text-zinc-300 hover:text-red-500 transition-colors"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {isEditing ? (
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full p-2 border border-zinc-200 rounded-md text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-900 min-h-[80px] resize-none"
                    autoFocus
                  />
                ) : (
                  <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">
                    {note.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-100">
                  <span className="text-xs text-zinc-500 font-medium">
                    {note.owner_name || 'Unknown'}
                  </span>
                  <span className="text-xs text-zinc-400">
                    {formatDate(note.created_at)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-white border border-zinc-100 rounded-lg">
          <StickyNote className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            No notes yet
          </h3>
          <p className="text-zinc-500 mb-6">
            Add your first note to share with your team
          </p>
          <Button
            onClick={() => setShowAddSheet(true)}
            className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm px-6 py-5 text-xs uppercase tracking-widest"
          >
            Add Note
          </Button>
        </div>
      )}

      {/* Add Note Sheet */}
      <Sheet open={showAddSheet} onOpenChange={setShowAddSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="add-note-sheet">
          <SheetHeader className="pb-6 border-b border-zinc-100">
            <SheetTitle className="text-2xl text-zinc-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Add Note
            </SheetTitle>
            <p className="text-sm text-zinc-500 mt-1">Capture an important thought or idea</p>
          </SheetHeader>
          
          <div className="space-y-5 mt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Category
                </label>
                <Select
                  value={newNote.category}
                  onValueChange={(value) => setNewNote({ ...newNote, category: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-note-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => {
                      const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG["Miscellaneous"];
                      const CatIcon = config.icon;
                      return (
                        <SelectItem key={cat} value={cat}>
                          <div className="flex items-center gap-2">
                            <CatIcon className="w-4 h-4" />
                            {cat}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Visibility
                </label>
                <Select
                  value={newNote.visibility}
                  onValueChange={(value) => setNewNote({ ...newNote, visibility: value })}
                >
                  <SelectTrigger className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 focus:ring-2 focus:ring-zinc-900" data-testid="add-note-visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Private (Only Me)
                      </div>
                    </SelectItem>
                    <SelectItem value="shared">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Shared (Visible to All)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={newNote.description}
                onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all min-h-[150px] resize-none"
                placeholder="Write your note here..."
                data-testid="add-note-description-input"
              />
            </div>

            <Button
              onClick={handleCreateNote}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-md py-4 text-sm font-medium tracking-wide transition-all mt-4"
              data-testid="save-note-btn"
            >
              Save Note
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ImportantNotes;
